import type { Task, TaskPhase, TaskAdditionalData } from '../types';
import { listVersions, resolveCurrentTemplate, type TemplateEntry } from '../../tradeTemplates/services/TradeTemplateVersionService';

/**
 * Coerce a stored additionalData value (empty-string sentinel, a plain object, or a
 * JSON-encoded string) into the {@link TaskAdditionalData} shape the UI expects.
 */
function normalizeAdditionalData(raw: unknown): TaskAdditionalData {
  if (raw == null || raw === '' || raw === '{}') return '';
  if (raw && typeof raw === 'object') {
    return Object.keys(raw).length ? raw : '';
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && Object.keys(parsed).length
        ? (parsed as TaskAdditionalData)
        : '';
    } catch {
      return '';
    }
  }
  return '';
}

/** Read a value as a positive finite number, or undefined. */
function asPositiveNumber(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Read a value as a non-empty display label (string or number), or undefined. */
function asLabel(value: unknown): string | undefined {
  if (typeof value === 'string') return value.length ? value : undefined;
  if (typeof value === 'number') return String(value);
  return undefined;
}

/**
 * Map a raw task object (any current or future shape) onto the thin UI {@link Task},
 * preserving the original verbatim in `_raw` so unknown fields survive a save round-trip.
 * Only the displayed/edited fields are surfaced; everything else stays opaque.
 */
export function toTask(raw: Record<string, unknown>, index: number): Task {
  // UI identity must be a stable, unique number. task_order is unique & numeric in the
  // task_uid format; fall back to a legacy numeric id, then the row position. This id is
  // UI-only (drag/drop, link targeting) and is never written back to the stored task.
  const id = asPositiveNumber(raw.task_order) ?? asPositiveNumber(raw.id) ?? index + 1;
  // Displayed task number — the WBS-style task_uid in the new format; legacy wbs/id otherwise.
  const taskNumber = asLabel(raw.task_uid) ?? asLabel(raw.wbs) ?? asLabel(raw.id) ?? String(id);
  return {
    id,
    phase: (raw.phase as TaskPhase) ?? 'activeConstruction',
    taskName: asLabel(raw.taskName) ?? '',
    additionalData: normalizeAdditionalData(raw.additionalData),
    taskNumber,
    // Surfaced so the "cascade dependents" affordance keeps working. In the task_uid format
    // dependencies reference task_order values, which is exactly what `id` is set to above.
    dependencies: asLabel(raw.dependencies) ?? '',
    // Surfaced so the populate-link rule "target must be an Install Task" keeps working.
    taskType: asLabel(raw.taskType) ?? '',
    _raw: raw,
  };
}

/** Flatten per-phase template entries' raw task JSON into the mapping's UI Task[] (via {@link toTask}). */
function entriesToTasks(entries: TemplateEntry[]): Task[] {
  const out: Task[] = [];
  for (const e of entries) {
    try {
      const parsed = JSON.parse(e.tasks ?? '[]');
      if (Array.isArray(parsed)) {
        parsed.forEach((t, i) => out.push(toTask((t ?? {}) as Record<string, unknown>, out.length + i)));
      }
    } catch {
      // skip a malformed entry — keep the rest of the template usable
    }
  }
  return out;
}

/**
 * Load the LATEST published task template for a specific scope, as the mapping's UI Task[].
 *   • tenantId === null → the trade's provider default (resolver falls back to the Global Default).
 *   • tenantId set      → that tenant's current published version only.
 * Used by the Mapping tab so the tasks shown/edited belong to the chosen tenant/provider.
 */
export async function getTaskTemplateForScope(
  tradeId: number,
  tenantId: string | null,
  accessToken: string,
): Promise<Task[]> {
  if (!tenantId) {
    const r = await resolveCurrentTemplate(tradeId, accessToken);
    return r.ok && r.data ? entriesToTasks(r.data.entries ?? []) : [];
  }
  const r = await listVersions(tradeId, 'tenant', tenantId, accessToken);
  const versions = r.ok ? (r.data?.versions ?? []) : [];
  if (!versions.length) return [];
  // listVersions returns oldest-first; the live version is the one flagged current (invalid=false).
  const current = versions.find(v => v.current) ?? versions[versions.length - 1];
  return entriesToTasks(current?.entries ?? []);
}
